import { useState } from 'react';
import { useCampaigns } from '@/hooks/useCampaigns';
import { CampaignCard } from '@/components/cards/CampaignCard';
import { ClientDetailModal } from '@/components/common/ClientDetailModal';
import { SearchBar } from '@/components/filters/SearchBar';
import { SortMenu } from '@/components/filters/SortMenu';
import { GroupingMenu } from '@/components/filters/GroupingMenu';
import { CardSkeleton } from '@/components/common/SkeletonLoader';
import { Megaphone, RefreshCw, Eye, EyeOff, ChevronDown, ChevronRight, Download, Users, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import type { SortField, GroupBy, CampaignClient } from '@/types/appointments';

const SORT_OPTIONS: Array<{ value: SortField; label: string }> = [
  { value: 'daysAbsent', label: 'Días ausente' },
  { value: 'name', label: 'Nombre' },
  { value: 'totalSales', label: 'Total ventas' },
  { value: 'totalAppointments', label: 'Total citas' },
  { value: 'outstandingAmount', label: 'Saldo deudor' },
];

const CAMPAIGN_GROUP_OPTIONS = [
  { value: 'none', label: 'Sin agrupar' },
  { value: 'days_absent_range', label: 'Por inactividad' },
  { value: 'staff', label: 'Por profesional' },
  { value: 'sales_range', label: 'Por consumo' },
  { value: 'visits_range', label: 'Por visitas' },
  { value: 'debt_status', label: 'Por estado de deuda' },
];

export function CampaignsPage() {
  const {
    clients,
    groupedClients,
    totalClientsCount,
    totalSegmentCounts,
    pendingSegmentCounts,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    activeCampaignSegment,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    fetchCampaigns,
    setActiveSegment,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
  } = useCampaigns();

  const [selectedModalClient, setSelectedModalClient] = useState<CampaignClient | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const tabs = [
    {
      id: 'lapsed' as const,
      label: 'Inactivos',
      pending: pendingSegmentCounts.lapsed,
      total: totalSegmentCounts.lapsed,
    },
    {
      id: 'vip' as const,
      label: 'VIP',
      pending: pendingSegmentCounts.vip,
      total: totalSegmentCounts.vip,
    },
    {
      id: 'debtor' as const,
      label: 'Deudores',
      pending: pendingSegmentCounts.debtor,
      total: totalSegmentCounts.debtor,
    },
  ];

  return (
    <div className="space-y-4 py-4 max-w-6xl mx-auto w-full">
      {/* Header & Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Campañas de Retención</h2>
          <p className="text-xs text-text-muted">
            {totalClientsCount} clientes analizados
          </p>
        </div>
        <button
          onClick={fetchCampaigns}
          className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary cursor-pointer"
          title="Recargar"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Segment Tabs */}
      <div className="flex gap-2 p-1 bg-surface-secondary rounded-xl">
        {tabs.map((tab) => {
          const isActive = activeCampaignSegment === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSegment(tab.id)}
              className={clsx(
                'flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all text-center cursor-pointer',
                isActive
                  ? 'bg-surface text-brand-primary shadow-xs'
                  : 'text-text-secondary hover:text-text-primary'
              )}
            >
              <span>{tab.label}</span>
              <span
                className={clsx(
                  'ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]',
                  isActive
                    ? 'bg-brand-primary/10 text-brand-primary font-bold'
                    : 'bg-surface-tertiary text-text-muted'
                )}
                title={`${tab.pending} pendientes de ${tab.total}`}
              >
                {tab.pending}{tab.pending !== tab.total && <span className="opacity-60">/{tab.total}</span>}
              </span>
            </button>
          );
        })}
      </div>

      {/* Loading Phase Progress Bar */}
      {loading && (
        <div className="space-y-1 bg-surface-secondary/60 p-2.5 rounded-xl border border-border">
          <div className="flex justify-between text-[11px] text-text-muted">
            <span className="font-medium text-brand-primary flex items-center gap-1.5">
              {loadingPhase === 'retention' && (
                <>
                  <Download size={13} className="shrink-0" />
                  <span>Obteniendo reporte de retención ({loadedCount}{totalCount ? `/${totalCount}` : ''})...</span>
                </>
              )}
              {loadingPhase === 'customers' && (
                <>
                  <Users size={13} className="shrink-0" />
                  <span>Actualizando lista de clientes ({loadedCount}{totalCount ? `/${totalCount}` : ''})...</span>
                </>
              )}
              {loadingPhase === 'idle' && (
                <>
                  <Loader2 size={13} className="shrink-0 animate-spin" />
                  <span>Procesando datos de clientes...</span>
                </>
              )}
            </span>
          </div>
          <div className="w-full bg-surface-tertiary rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-brand-primary h-1.5 rounded-full transition-all duration-300 animate-pulse"
              style={{ width: totalCount ? `${Math.min(100, Math.round((loadedCount / totalCount) * 100))}%` : '60%' }}
            />
          </div>
        </div>
      )}

      {/* Background Stats Progress bar */}
      {!loading && statsProgress.total > 0 && statsProgress.current < statsProgress.total && (
        <div className="space-y-1">
          <div className="flex justify-between text-[10px] text-text-muted">
            <span>Cargando estadísticas e historial en segundo plano...</span>
            <span>
              {statsProgress.current}/{statsProgress.total}
            </span>
          </div>
          <div className="w-full bg-surface-secondary rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-brand-primary h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(statsProgress.current / statsProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters & Inbox Zero Toggle */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Buscar por cliente, teléfono, estilista..."
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={() => setHideProcessed(!hideProcessed)}
            className={clsx(
              'flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-all cursor-pointer font-medium',
              hideProcessed
                ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                : 'bg-surface-secondary border-border text-text-secondary hover:text-text-primary'
            )}
            title={hideProcessed ? 'Mostrando solo pendientes' : 'Mostrando todos los clientes'}
          >
            {hideProcessed ? <EyeOff size={13} /> : <Eye size={13} />}
            <span>{hideProcessed ? 'Solo pendientes' : 'Ver todos'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <GroupingMenu
            options={CAMPAIGN_GROUP_OPTIONS}
            selected={groupBy}
            onChange={(val) => setGroupBy(val as GroupBy)}
          />
          <SortMenu
            options={SORT_OPTIONS}
            selected={sortBy}
            order={sortOrder}
            onSort={(field) => setSortBy(field as SortField)}
            onToggleOrder={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Client List */}
      {loading && clients.length === 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <Megaphone size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay clientes en este segmento</p>
        </div>
      ) : groupBy === 'none' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
          {clients.map((client) => (
            <CampaignCard
              key={client.id}
              client={client}
              isProcessed={isProcessed(client.id)}
              onMarkProcessed={() =>
                isProcessed(client.id) ? unmarkProcessed(client.id) : markProcessed(client.id)
              }
              onOpenDetails={setSelectedModalClient}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedClients).map(([groupLabel, groupList]) => {
            const isCollapsed = !!collapsedGroups[groupLabel];
            return (
              <div key={groupLabel} className="space-y-2.5">
                {groupLabel && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupLabel)}
                    className="w-full flex items-center justify-between py-2.5 px-3.5 bg-surface/95 backdrop-blur-md hover:bg-surface rounded-xl border border-border mt-2 shadow-2xs cursor-pointer transition-colors text-left sticky top-0 z-10"
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight size={15} className="text-text-muted transition-transform" />
                      ) : (
                        <ChevronDown size={15} className="text-text-muted transition-transform" />
                      )}
                      <span className="text-xs font-semibold text-text-primary">{groupLabel}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-text-muted border border-border/50">
                      {groupList.length}
                    </span>
                  </button>
                )}
                {!isCollapsed && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start pt-1">
                    {groupList.map((client) => (
                      <CampaignCard
                        key={client.id}
                        client={client}
                        isProcessed={isProcessed(client.id)}
                        onMarkProcessed={() =>
                          isProcessed(client.id) ? unmarkProcessed(client.id) : markProcessed(client.id)
                        }
                        onOpenDetails={setSelectedModalClient}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Client Detail Modal */}
      <ClientDetailModal
        client={selectedModalClient as any}
        isOpen={Boolean(selectedModalClient)}
        onClose={() => setSelectedModalClient(null)}
      />
    </div>
  );
}
